import { useCallback } from 'react';

import {
    getMessageUploadUrl,
    scanMessageFile
} from '../redux/attachments/service';
import { uploadFileWithProgress } from '../utils/attachments';
import useAppDispatch from './useAppDispatch';
import { UploadedFile } from './useFileUploadWithValidation';

interface UseMessageFileUploadOptions {
    conversationId: number;
    onFilesUpdate: (
        updater: (prevFiles: UploadedFile[]) => UploadedFile[]
    ) => void;
    onFileComplete?: (attachmentId: number) => void;
    onFileRemove?: (attachmentId: number) => void;
}

// Similar to useFileUploadWithValidation, but targets the waggle service instead of the
// backend attachment service. If a shared attachments service is extracted in the future,
// these two hooks should be consolidated.
const MAX_FILE_SIZE = 20 * 1024 * 1024;

interface UseMessageFileUploadReturn {
    handleFilesSelect: (
        filesWithIds: { file: File; id: number }[]
    ) => Promise<void>;
    removeFile: (localId: number) => void;
}

const useMessageFileUpload = ({
    conversationId,
    onFilesUpdate,
    onFileComplete,
    onFileRemove
}: UseMessageFileUploadOptions): UseMessageFileUploadReturn => {
    const dispatch = useAppDispatch();

    const removeFile = useCallback(
        (localId: number) => {
            onFilesUpdate((prev) => {
                const file = prev.find((f) => f.id === localId);
                if (file?.fileId != null) {
                    file.uploadController?.abort();
                    onFileRemove?.(file.fileId);
                }
                return prev.filter((f) => f.id !== localId);
            });
        },
        [onFilesUpdate, onFileRemove]
    );

    const handleFilesSelect = useCallback(
        async (filesWithIds: { file: File; id: number }[]): Promise<void> => {
            const allFiles = filesWithIds.map(({ file, id }) => ({
                id,
                file,
                uploadProgress: 0,
                status:
                    file.size > MAX_FILE_SIZE
                        ? ('error' as const)
                        : ('uploading' as const),
                errorMessage:
                    file.size > MAX_FILE_SIZE
                        ? `${file.name} exceeds the 20 MB limit`
                        : undefined
            }));

            onFilesUpdate((prev) => [...prev, ...allFiles]);

            for (const attachedFile of allFiles) {
                if (attachedFile.status === 'error') {
                    continue;
                }

                let attachmentId: number;
                try {
                    const response = await dispatch(
                        getMessageUploadUrl({
                            fileName: attachedFile.file.name,
                            fileType: attachedFile.file.type,
                            conversationId
                        })
                    ).unwrap();

                    const uploadUrl = response.url;
                    attachmentId = response.fileId;

                    const uploadController = new AbortController();

                    onFilesUpdate((prev) =>
                        prev.map((f) =>
                            f.id === attachedFile.id
                                ? {
                                      ...f,
                                      fileId: attachmentId,
                                      uploadController
                                  }
                                : f
                        )
                    );

                    const uploadResult = await uploadFileWithProgress({
                        uploadUrl,
                        file: attachedFile.file,
                        fileId: attachedFile.id,
                        signal: uploadController.signal,
                        onProgress: (fileId, progress) => {
                            onFilesUpdate((prev) =>
                                prev.map((f) =>
                                    f.id === fileId
                                        ? { ...f, uploadProgress: progress }
                                        : f
                                )
                            );
                        },
                        onComplete: (fileId, success) => {
                            if (success) {
                                onFilesUpdate((prev) =>
                                    prev.map((f) =>
                                        f.id === fileId
                                            ? {
                                                  ...f,
                                                  status: 'completed' as const
                                              }
                                            : f
                                    )
                                );
                            }
                        }
                    });

                    if (!uploadResult.success) {
                        onFilesUpdate((prev) =>
                            prev.map((f) =>
                                f.id === attachedFile.id
                                    ? {
                                          ...f,
                                          status: 'error' as const,
                                          errorMessage: `Failed to upload ${attachedFile.file.name}, please try again`
                                      }
                                    : f
                            )
                        );
                        continue;
                    }

                    const scanResult = await dispatch(
                        scanMessageFile({ attachmentId })
                    ).unwrap();

                    if (!scanResult.isSafe) {
                        onFilesUpdate((prev) =>
                            prev.filter((f) => f.id !== attachedFile.id)
                        );
                        onFileRemove?.(attachmentId);
                    } else {
                        onFileComplete?.(attachmentId);
                    }
                } catch (error) {
                    console.error('Upload error:', error);
                    onFilesUpdate((prev) =>
                        prev.map((f) =>
                            f.id === attachedFile.id
                                ? {
                                      ...f,
                                      status: 'error' as const,
                                      errorMessage: `Failed to upload ${attachedFile.file.name}, please try again`
                                  }
                                : f
                        )
                    );
                }
            }
        },
        [dispatch, conversationId, onFilesUpdate, onFileComplete, onFileRemove]
    );

    return { handleFilesSelect, removeFile };
};

export default useMessageFileUpload;
